/**
 * Loader Component
 *
 * A full-screen loading spinner that indicates content is being loaded.
 * This component provides a consistent loading experience throughout the application.
 *
 * Features:
 * - Centered in the viewport
 * - Adjustable height calculation
 * - Consistent styling and animation
 *
 * @module components/loader/loader
 */

import Spinner from "react-bootstrap/Spinner";
import styled from "styled-components";

/**
 * Container for the spinner that centers it in the available space
 * Height is calculated based on viewport height minus a header offset
 */
const LoaderDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ $calcHeight }) =>
    $calcHeight ? `calc(100vh - ${$calcHeight}px)` : "calc(100vh - 56px)"};
`;

/**
 * Styled spinner with larger dimensions for better visibility
 */
const SpinnerElement = styled(Spinner)`
  width: 80px;
  height: 80px;
`;

/**
 * Loader component that displays a centered spinner
 *
 * @param {Object} props - Component properties
 * @param {number|null} [props.calcHeight=null] - Custom height offset in pixels
 * @returns {JSX.Element} Loader component
 */
const Loader = ({ calcHeight = null }) => {
  return (
    <LoaderDiv $calcHeight={calcHeight}>
      <SpinnerElement animation="border" />
    </LoaderDiv>
  );
};

export default Loader;
